import React, {
	createContext, useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import useShots from '../shots/provider';
import { GreenroomState } from '../../stores/types/types';
import type { ShotFeed } from '../../stores/types/shots';
import useCanvasConfig from '../../hooks/use-canvas-config';

interface PreloadedInfo {
	videoIndices:(number|undefined)[],
	type: 'media' | 'audio' | 'presenter' | 'screenshare' | 'sipCall' | 'srtStream' | 'onsiteLocation',
	position: number
}

type ShotFeedMap = Map<string, Map<number | string, PreloadedInfo>>;

interface VideoCacheContextType {
	preloadedElements: null | React.MutableRefObject<ShotFeedMap>;
}

export const VideoCacheContext = createContext<VideoCacheContextType>({
	preloadedElements: null,
});

interface Props {
	children: React.ReactNode;
}

export const UseVideoCache = (): VideoCacheContextType => {
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);

	// GREEN-6263 - used to re-render the hook so child components have the
	// correct preloadedElements map. Without this, black feeds may occur.
	const [count, setCount] = React.useState(0);
	const preloadedElements = useRef<ShotFeedMap>(new Map());

	const { currentShot } = useShots();

	// the next shot in the show
	const nextShot = useMemo(() => {
		const nextShotOrder = currentShot.shot_data.order + 1;
		const nNextShot = shots.find((shot) => shot.shot_data.order === nextShotOrder);
		return nNextShot;
	}, [currentShot, shots]);

	const { shotFeeds } = useCanvasConfig(currentShot);
	const { shotFeeds: nextShotFeeds } = useCanvasConfig(nextShot);

	// used to re-render the hook and attached new videos to the dom in a useEffect
	useEffect(() => {
		const newUrlsByMediaId: ShotFeedMap = new Map();
		newUrlsByMediaId.set('current-shot', new Map());
		newUrlsByMediaId.set('next-shot', new Map());

		const assignToMap = (feed: ShotFeed, subMap: string) => {
			const {
				media: mediaId,
				feedType,
				presenter,
				position,
				screenshare,
			} = feed;

			const nFeed = { videoIndices: [1], type: feedType, position } as PreloadedInfo;
			if ((feedType === 'media' || feedType === 'audio') && (mediaId || mediaId === 0)) {
				newUrlsByMediaId.get(subMap)?.set(mediaId, nFeed);
			} else if (feedType === 'presenter' && presenter) {
				newUrlsByMediaId.get(subMap)?.set(presenter, nFeed);
			} else if ((feedType === 'screenshare' && screenshare)) {
				newUrlsByMediaId.get(subMap)?.set(screenshare, nFeed);
			} else if ((feedType === 'sipCall' && feed.sipStreamId)) {
				newUrlsByMediaId.get(subMap)?.set(feed.sipStreamId, nFeed);
			} else if ((feedType === 'srtStream' && feed.srtStreamId)) {
				newUrlsByMediaId.get(subMap)?.set(feed.srtStreamId, nFeed);
			} else if ((feedType === 'onsiteLocation' && feed.onsiteLocationId)) {
				newUrlsByMediaId.get(subMap)?.set(feed.onsiteLocationId, nFeed);
			}
		};

		shotFeeds.forEach((feed: ShotFeed) => assignToMap(feed, 'current-shot'));
		nextShotFeeds.forEach((feed: ShotFeed) => assignToMap(feed, 'next-shot'));

		// whenever the shot changes, this will trigger
		// and update the preloadedElements map
		// newUrlsByMediaId.forEach(createAndAttachVideos);
		preloadedElements.current = newUrlsByMediaId;
		setCount(count + 1);
	}, [
		shotFeeds,
		nextShotFeeds,
	]);

	return { preloadedElements };
};

const VideoCacheProvider: React.FC<Props> = ({ children }: Props) => {
	const videoCacheContext = UseVideoCache();
	return <VideoCacheContext.Provider value={videoCacheContext}>
		{children}
	</VideoCacheContext.Provider>;
};

export default VideoCacheProvider;
